'use client';
import { useCallback, useMemo, useRef, useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react';
import { clsx } from 'clsx';
import parse from 'html-react-parser';
import { Accordion } from 'components/UI/Accordion';
import { Input } from 'components/UI/Input';
import { generateWeakKey } from 'utils/idGenerator';
import styles from './Quiz.module.scss';

export interface QuizQuestion {
  readonly type: 'choice' | 'integer';
  readonly question: string;
  readonly afterText?: string;
  readonly options?: ReadonlyArray<{
    readonly text: string;
    readonly points: number;
  }>;
  readonly inputConfig?: {
    readonly label: string;
    readonly placeholder: string;
    readonly min?: number;
    readonly max?: number;
  };
  readonly explanation?: {
    readonly title: string;
    readonly answers: ReadonlyArray<string>;
  };
}

export type IntegerQuizQuestion = QuizQuestion & {
  type: 'integer';
  inputConfig: {
    label: string;
    placeholder: string;
    min?: number;
    max?: number;
  };
};

export type ChoiceQuizQuestion = QuizQuestion & {
  type: 'choice';
  options: ReadonlyArray<{ text: string; points: number }>;
};

type QuizAnswer = {
  readonly index: number;
  readonly text: string;
  readonly points: number;
};

type QuizState = {
  readonly currentQuestion: number;
  readonly inputValue: string;
  readonly selectedAnswers: Record<string, number>;
  readonly answers: ReadonlyArray<QuizAnswer>;
};

type QuizProps<T> = {
  readonly data: ReadonlyArray<QuizQuestion | IntegerQuizQuestion | ChoiceQuizQuestion>;
  readonly renderResult: (state: QuizState, calculatedResult: T) => ReactNode;
  readonly calculateResult: (state: QuizState) => T;
  readonly className?: string;
  readonly startButtonText?: string;
  readonly nextButtonText?: string;
  readonly previousButtonText?: string;
  readonly calculateButtonText?: string;
  readonly recalculateButtonText?: string;
};

const isChoiceQuestion = (question: QuizQuestion): question is ChoiceQuizQuestion => {
  return question.type === 'choice';
};

const isIntegerQuestion = (question: QuizQuestion): question is IntegerQuizQuestion => {
  return question.type === 'integer';
};

type ViewState = 'start' | 'questions' | 'results';

export const Quiz = <T,>({
  data,
  startButtonText = 'Quiz starten',
  nextButtonText = 'nächste Frage',
  previousButtonText = 'vorherige Frage',
  calculateButtonText = 'Ergebnis berechnen',
  recalculateButtonText = 'Neu berechnen',
  renderResult,
  calculateResult,
  className,
}: QuizProps<T>) => {
  const componentRef = useRef<HTMLDivElement>(null);

  const [viewState, setViewState] = useState<ViewState>('start');
  const [quizState, setQuizState] = useState<Omit<QuizState, 'answers'>>({
    currentQuestion: 0,
    inputValue: '',
    selectedAnswers: {},
  });

  const computedAnswers = useMemo<ReadonlyArray<QuizAnswer>>(() => {
    const out: Array<QuizAnswer> = [];
    for (let i = 0; i < data.length; i++) {
      const key = `question_${i}`;
      const idx = quizState.selectedAnswers[key];
      if (idx === undefined) continue;

      const q = data[i];
      if (isChoiceQuestion(q)) {
        const opt = q.options[idx];
        if (!opt) continue;
        out[i] = { index: idx, text: opt.text, points: opt.points };
      } else {
        out[i] = { index: idx, text: quizState.inputValue, points: 0 };
      }
    }
    return out.filter(Boolean);
  }, [data, quizState.selectedAnswers, quizState.inputValue]);

  const visibleState: QuizState = useMemo(
    () => ({ ...quizState, answers: computedAnswers }),
    [quizState, computedAnswers]
  );

  const calculatedResult = useMemo(() => calculateResult(visibleState), [visibleState, calculateResult]);

  const accordionItems = useMemo(() => {
    return visibleState.answers
      .map((answer, index) => {
        const question = data[index + 1];
        if (!question || !isChoiceQuestion(question) || !question.explanation) {
          return { title: '', content: <></> };
        }
        return {
          title: question.explanation.title ?? '',
          content: (
            <>
              <div className={styles.answer}>Ihre Antwort: {answer.text}</div>
              {question.explanation?.answers && <div>{parse(question.explanation.answers[answer.index])}</div>}
            </>
          ),
        };
      })
      .filter((item) => item.title);
  }, [visibleState.answers, data]);

  const isStartView = viewState === 'start';
  const isQuestionsView = viewState === 'questions';
  const isResultsView = viewState === 'results';

  const handleStartClick = useCallback(() => {
    setViewState('questions');
    setQuizState((prev) => ({ ...prev, currentQuestion: 0 }));
  }, []);

  const handleAnswerChange = useCallback((questionId: string, answerIndex: number) => {
    setQuizState((prev) => ({
      ...prev,
      selectedAnswers: { ...prev.selectedAnswers, [questionId]: answerIndex },
    }));
  }, []);

  const handleSubmit = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      if (quizState.currentQuestion === data.length - 1) {
        setViewState('results');
      } else {
        setQuizState((prev) => ({ ...prev, currentQuestion: prev.currentQuestion + 1 }));
      }
    },
    [quizState.currentQuestion, data.length]
  );

  const previousQuestion = useCallback(() => {
    setQuizState((prev) => ({ ...prev, currentQuestion: Math.max(0, prev.currentQuestion - 1) }));
  }, []);

  const handleInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setQuizState((prev) => ({ ...prev, inputValue: event.target.value }));
  }, []);

  const reset = useCallback(() => {
    setViewState('start');
    setQuizState({ currentQuestion: 0, inputValue: '', selectedAnswers: {} });
    componentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return (
    <div ref={componentRef} className={clsx(styles.root, className)}>
      {isStartView && (
        <div className={styles.buttonWrapper}>
          <button className={styles.button} type="button" onClick={handleStartClick}>
            {startButtonText}
          </button>
        </div>
      )}

      {isQuestionsView && (
        <div>
          <div className={styles.questionHeadline}>
            Frage {quizState.currentQuestion + 1}/{data.length}
          </div>

          <div className={styles.intro}>
            <p className={styles.subheadline}>{parse(data[quizState.currentQuestion].question)}</p>
          </div>

          <form onSubmit={handleSubmit}>
            {isIntegerQuestion(data[quizState.currentQuestion]) ? (
              <Input
                id={`question_${quizState.currentQuestion}_input`}
                type="number"
                label={(data[quizState.currentQuestion] as IntegerQuizQuestion).inputConfig.label}
                value={quizState.inputValue}
                onChange={handleInputChange}
                min={(data[quizState.currentQuestion] as IntegerQuizQuestion).inputConfig.min}
                max={(data[quizState.currentQuestion] as IntegerQuizQuestion).inputConfig.max}
                placeholder={(data[quizState.currentQuestion] as IntegerQuizQuestion).inputConfig.placeholder}
                required
              />
            ) : (
              <div>
                {isChoiceQuestion(data[quizState.currentQuestion]) &&
                  (data[quizState.currentQuestion] as ChoiceQuizQuestion).options.map((answer, index) => {
                    const inputId = generateWeakKey(answer, `question_${quizState.currentQuestion}_option`);
                    return (
                      <label
                        key={generateWeakKey(answer, `label_${quizState.currentQuestion}`)}
                        className={styles.radioLabel}
                        htmlFor={inputId}
                      >
                        <input
                          type="radio"
                          id={inputId}
                          className={styles.radio}
                          name={`question_${quizState.currentQuestion}`}
                          value={index}
                          onChange={() => handleAnswerChange(`question_${quizState.currentQuestion}`, index)}
                          checked={quizState.selectedAnswers[`question_${quizState.currentQuestion}`] === index}
                          required
                        />
                        <span>{answer.text}</span>
                      </label>
                    );
                  })}
              </div>
            )}

            {data[quizState.currentQuestion].afterText && (
              <div className={styles.intro}>
                <p>{parse(data[quizState.currentQuestion].afterText!)}</p>
              </div>
            )}

            <div className={styles.buttonWrapper}>
              {quizState.currentQuestion > 0 && (
                <button type="button" className={clsx(styles.button, styles.left)} onClick={previousQuestion}>
                  {previousButtonText}
                </button>
              )}
              <button type="submit" className={clsx(styles.button, styles.right)}>
                {quizState.currentQuestion === data.length - 1 ? calculateButtonText : nextButtonText}
              </button>
            </div>
          </form>
        </div>
      )}

      {isResultsView && (
        <div aria-live="polite">
          {renderResult(visibleState, calculatedResult)}
          {accordionItems.length > 0 && <Accordion items={accordionItems} type="multiple" />}

          <div className={styles.buttonWrapper}>
            <button type="button" className={styles.button} onClick={reset}>
              {recalculateButtonText}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
